// Librairies
import { v4 as uuidv4 } from 'uuid';
// Utils
import { submitFieldWrite } from './FieldWrite';
import { getOfflineServices } from './OfflineServices';
import { getDisplayFeature } from './ProjectSnapshot';
import { diffFeatures, toPlainFeature } from './MergeUtil';
import { cleanupRemovedOps } from './LocalCleanup';
import { getElementRefs } from './handlers/ElementRefs';
import { ELEMENT_CATEGORIES } from './handlers/ElementHandlers';

// Écritures d'éléments par la file, communes à toutes les catégories ; chaque service garde le traitement de la réponse et ses toasts

// Espace vert issu d'une découpe ou d'une fusion : greenSpaceId liste ses origines (l'API y reprend historique, occurrences et
// photos), dont la suppression doit attendre la création
const getOriginIds = (category, feature) => (category === 'Espace vert' && feature.properties.greenSpaceId && feature.properties.greenSpaceId !== feature.id
    ? feature.properties.greenSpaceId.split(',')
    : []);

// Copie locale d'un morceau : son propre id, comme la réponse de l'API ; rouvert depuis la copie, l'id de son origine le ferait
// redécouper ou modifier comme l'origine (la file garde les origines pour l'API)
const withOwnGreenSpaceId = (category, feature) => (getOriginIds(category, feature).length
    ? { ...feature, properties: { ...feature.properties, greenSpaceId: feature.id } }
    : feature);

// Paquets d'une écriture en masse : une seule opération géante serait longue à relire, envoyer et rejouer
const BULK_SIZE = 200;

// Création : la feature entière part, l'élément est affiché depuis la copie jusqu'à l'acceptation
export const createElement = (category, sourceFeature) => {
    const feature = toPlainFeature(sourceFeature);
    const projectId = feature.projectId;
    return submitFieldWrite({
        op: {
            type: 'element.create', projectId, target: { entity: 'element', id: feature.id, category },
            touches: [`element:${feature.id}`, ...getOriginIds(category, feature).map(id => `element:${id}`)], payload: { feature }, refs: getElementRefs(feature),
            softWaitFor: getOriginIds(category, feature).map(id => `element:${id}`)
        },
        applyLocal: services => services.snapshot.putLocal(projectId, category, withOwnGreenSpaceId(category, feature)),
        revertLocal: services => services.snapshot.revertLocal(projectId, feature.id),
        buildLocalData: () => sourceFeature
    });
};

// Création en masse (duplication, copier-coller, découpe) par paquets ; un résultat par paquet, local ou réponse de l'API
export const createElements = (category, sourceFeatures) => {
    const chunks = [];
    for (let i = 0; i < sourceFeatures.length; i += BULK_SIZE) chunks.push(sourceFeatures.slice(i, i + BULK_SIZE));
    // Clé commune aux paquets d'un lot, chacun dépend des précédents : un paquet refusé retire aussi les suivants (tout ou rien)
    const batchKey = `batch:${uuidv4()}`;
    return Promise.all(chunks.map(chunk => {
        const features = chunk.map(toPlainFeature);
        const projectId = features[0].projectId;
        const touches = [...[...new Set(features.flatMap(feature => [feature.id, ...getOriginIds(category, feature)]))].map(id => `element:${id}`), batchKey];
        return submitFieldWrite({
            op: {
                type: 'element.bulkCreate', projectId, target: { entity: 'elements', id: uuidv4(), category },
                touches, refs: features.flatMap(getElementRefs), payload: { features },
                softWaitFor: [...new Set(features.flatMap(feature => getOriginIds(category, feature)))].map(id => `element:${id}`)
            },
            applyLocal: services => Promise.all(features.map(feature => services.snapshot.putLocal(projectId, category, withOwnGreenSpaceId(category, feature)))),
            revertLocal: services => Promise.all(features.map(feature => services.snapshot.revertLocal(projectId, feature.id))),
            buildLocalData: () => chunk
        });
    }));
};

// Seuls les champs changés depuis la feature d'avant la saisie partent ; repli sur la copie si l'appelant ne la donne pas
export const updateElement = async (category, id, sourceFeature, flags = {}, previousFeature = null) => {
    const feature = toPlainFeature(sourceFeature);
    const projectId = feature.projectId;
    const record = await getOfflineServices().snapshot.getElement(projectId, id);
    const before = previousFeature ? toPlainFeature(previousFeature) : getDisplayFeature(record);
    let { changes, base } = diffFeatures(before || { properties: {} }, feature);
    // Diff vide : l'appelant a modifié la feature en place, toute la feature part plutôt que rien
    if (!Object.keys(changes).length) {
        console.warn('[hors-ligne] modification sans champ modifié détecté, envoi de toute la feature', id);
        ({ changes, base } = diffFeatures({ properties: {} }, feature));
    }
    const { updateResultKey } = ELEMENT_CATEGORIES[category];
    return submitFieldWrite({
        op: {
            type: 'element.update', projectId, target: { entity: 'element', id, category },
            payload: { changes, flags: { history: !!flags.history, replanting: !!flags.replanting } },
            base: before ? base : null, refs: getElementRefs(feature),
            // Replantation : les photos déjà en file de l'arbre partent avant, elles restent à l'ancienne plantation
            softWaitFor: flags.replanting ? [`element:${id}`] : []
        },
        applyLocal: services => services.snapshot.putLocal(projectId, category, feature),
        revertLocal: services => (record?.local ? services.snapshot.putLocal(projectId, category, record.local) : services.snapshot.revertLocal(projectId, id)),
        // Même forme que la réponse de l'API
        buildLocalData: () => (updateResultKey ? { [updateResultKey]: sourceFeature, history: null } : sourceFeature)
    });
};

// Modification en masse par paquets : champs changés élément par élément, comme updateElement ; un résultat par paquet
export const updateElements = async (category, sourceFeatures, flags = {}, previousFeatures = null) => {
    const snapshot = getOfflineServices().snapshot;
    const chunks = [];
    for (let i = 0; i < sourceFeatures.length; i += BULK_SIZE) chunks.push(sourceFeatures.slice(i, i + BULK_SIZE).map((sourceFeature, j) => ({ sourceFeature, previousFeature: previousFeatures?.[i + j] })));
    return Promise.all(chunks.map(async allItems => {
        const chunk = [], items = [], records = [];
        for (const item of allItems) {
            const feature = toPlainFeature(item.sourceFeature);
            const record = await snapshot.getElement(feature.projectId, feature.id);
            const before = item.previousFeature ? toPlainFeature(item.previousFeature) : getDisplayFeature(record);
            let { changes, base } = diffFeatures(before || { properties: {} }, feature);
            if (!Object.keys(changes).length) {
                // Déjà à la valeur saisie : rien à envoyer (toute la feature écraserait les saisies des collègues), sauf modification en place
                if (item.previousFeature && item.previousFeature.properties !== item.sourceFeature.properties) continue;
                ({ changes, base } = diffFeatures({ properties: {} }, feature));
            }
            chunk.push({ ...item, feature });
            items.push({ id: feature.id, changes, base: before ? base : null });
            records.push(record);
        }
        if (!chunk.length) return { data: [] };
        const features = chunk.map(({ feature }) => feature);
        const projectId = features[0].projectId;
        return submitFieldWrite({
            op: {
                type: 'element.bulkUpdate', projectId, target: { entity: 'elements', id: uuidv4(), category },
                touches: features.map(feature => `element:${feature.id}`), refs: features.flatMap(getElementRefs),
                payload: { items, flags: { history: !!flags.history, replanting: !!flags.replanting } }
            },
            applyLocal: services => Promise.all(features.map(feature => services.snapshot.putLocal(projectId, category, feature))),
            revertLocal: services => Promise.all(features.map((feature, i) => (records[i]?.local
                ? services.snapshot.putLocal(projectId, category, records[i].local)
                : services.snapshot.revertLocal(projectId, feature.id)))),
            buildLocalData: () => chunk.map(({ sourceFeature }) => sourceFeature)
        });
    }));
};

// État passé ajouté à l'historique (réseau faible : l'onglet Historique est désactivé hors-ligne) ; attend la création de l'élément
export const addElementHistory = (category, sourceFeature, date) => {
    const feature = toPlainFeature(sourceFeature);
    return submitFieldWrite({
        op: {
            type: 'history.add', projectId: feature.projectId, target: { entity: 'history', id: uuidv4(), category },
            refs: [{ entity: 'element', id: feature.id }, ...getElementRefs(feature)], payload: { feature, date: date.toISOString() }
        },
        buildLocalData: () => undefined
    });
};

// Un élément créé sur l'appareil et jamais envoyé est simplement retiré ; les autres partent dans une seule suppression
export const removeElements = async (category, sourceFeatures) => {
    const projectId = sourceFeatures[0].projectId;
    const services = getOfflineServices();
    const keys = sourceFeatures.map(feature => `element:${feature.id}`);
    const { cancelledKeys, removedOps } = await services.outbox.cancelLocalCreations(keys);
    await cleanupRemovedOps(services, removedOps);
    await services.outbox.cancelPendingUpdates(keys.filter(key => !cancelledKeys.includes(key)));
    const ids = sourceFeatures.map(feature => feature.id);
    const createdLocally = ids.filter(id => cancelledKeys.includes(`element:${id}`));
    // Actions créées sur l'appareil pour ces éléments jamais envoyés : retirées de leur création, qui partirait vers un élément inexistant
    if (createdLocally.length)
        for (const op of await services.outbox.list())
            if (op.type === 'occurrences.create')
                for (const [elementId, localId] of Object.entries(op.payload.localIds))
                    if (createdLocally.includes(elementId)) await cleanupRemovedOps(services, (await services.outbox.cancelLocalOccurrence(localId)).removedOps);
    const remaining = sourceFeatures.filter(feature => !cancelledKeys.includes(`element:${feature.id}`)).map(toPlainFeature);
    if (!remaining.length) {
        await services.snapshot.markLocalDeleted(projectId, category, ids, { createdLocally });
        return { data: null, isLocal: true };
    }
    return submitFieldWrite({
        op: {
            type: 'element.delete', projectId, target: { entity: 'elements', id: uuidv4(), category },
            touches: remaining.map(feature => `element:${feature.id}`), payload: { features: remaining }
        },
        applyLocal: services => services.snapshot.markLocalDeleted(projectId, category, ids, { createdLocally }),
        revertLocal: services => Promise.all(remaining.map(feature => services.snapshot.revertLocal(projectId, feature.id))),
        buildLocalData: () => null
    });
};
